import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import {
  TORNEIO_POR_ID,
  TorneioPorIdData,
  TorneioPorIdVars,
  criarTorneio,
  TorneioInput,
  atualizarTorneio,
  TorneioProfile,
} from '../../services/torneioService';
import { useProfile } from '../../context/ProfileContext';
import { useLigaPermissions } from '../../hooks/useLigaPermissions';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';

registerLocale('pt-BR', ptBR);
setDefaultLocale('pt-BR');
import DescricaoModal from '../../components/admin/DescricaoModal';
import RegulamentoEditModal from '../../components/admin/RegulamentoEditModal';
import LocalModal, { LocalModalForm } from '../../components/admin/LocalModal';
import ProfilecardTournament from '../../components/admin/ProfilecardTournament';
import { upsertLocal, LocalInput, getLocalById, LocalDTO } from '../../services/localService';
import { StatusTorneio } from '../../utils/StatusTorneio';
import { Snackbar, Alert, Tooltip, IconButton } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DOMPurify from 'dompurify';
import TournamentOrganizersTab from './components/TournamentOrganizersTab';
import TournamentSponsorsTab from './components/TournamentSponsorsTab';
import TournamentDiscountsTab from './components/TournamentDiscountsTab';
import { PageBreadcrumb } from '../../components/navigation';

const EditLeagueTournament: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;
  const { canManageTournaments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  const isEditMode = !!id;

  // Estado para controlar aba ativa
  const [tab, setTab] = useState(0);

  // Query para buscar dados do torneio (apenas em modo edição)
  const { data: torneioData, loading: loadingTorneio, error: errorTorneio } = useQuery<
    TorneioPorIdData,
    TorneioPorIdVars
  >(TORNEIO_POR_ID, {
    variables: { id: id! },
    skip: !id || !isEditMode,
    fetchPolicy: 'no-cache',
  });

  // Estado do formulário
  const [form, setForm] = useState<{
    nome: string;
    username: string;
    subtitulo: string;
    descricao: string;
    regulamento: string;
    dataInicio: string;
    dataFim: string;
    dataInscricaoInicio: string;
    dataInscricaoFim: string;
    status: StatusTorneio | '';
    filaEspera: boolean;
    autoFilaEspera: boolean;
    exibirFilaEspera: boolean;
    camisetas: boolean;
    sapatos: boolean;
    atletaTrocaParceiro: boolean;
    exibirInscritos: boolean;
    autoCancNaoPagas: boolean;
    validarElegibilidade: boolean;
  }>({
    nome: '',
    username: '',
    subtitulo: '',
    descricao: '',
    regulamento: '',
    dataInicio: '',
    dataFim: '',
    dataInscricaoInicio: '',
    dataInscricaoFim: '',
    status: '',
    filaEspera: false,
    autoFilaEspera: false,
    exibirFilaEspera: false,
    camisetas: false,
    sapatos: false,
    atletaTrocaParceiro: false,
    exibirInscritos: false,
    autoCancNaoPagas: false,
    validarElegibilidade: false,
  });

  // Estado do local
  const [local, setLocal] = useState<LocalDTO | null>(null);
  const [localFormData, setLocalFormData] = useState<Partial<LocalModalForm> | null>(null);

  // Estados de UI
  const [isDescricaoModalOpen, setIsDescricaoModalOpen] = useState(false);
  const [isRegulamentoModalOpen, setIsRegulamentoModalOpen] = useState(false);
  const [isLocalModalOpen, setIsLocalModalOpen] = useState(false);
  const [savingLocal, setSavingLocal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = useState<{
    nome?: string;
    dataInicio?: string;
    dataFim?: string;
    dataInscricaoInicio?: string;
    dataInscricaoFim?: string;
  }>({});

  // Carregar dados do torneio em modo edição
  useEffect(() => {
    if (torneioData?.torneioPorId && isEditMode) {
      const t = torneioData.torneioPorId;
      setForm({
        nome: t.nome || '',
        username: t.username || '',
        subtitulo: t.subtitulo || '',
        descricao: t.descricao || '',
        regulamento: t.regulamento || '',
        dataInicio: t.dataInicio ? t.dataInicio.slice(0, 10) : '',
        dataFim: t.dataFim ? t.dataFim.slice(0, 10) : '',
        dataInscricaoInicio: t.dataInscricaoInicio ? t.dataInscricaoInicio.slice(0, 10) : '',
        dataInscricaoFim: t.dataInscricaoFim ? t.dataInscricaoFim.slice(0, 10) : '',
        status: t.status || '',
        filaEspera: t.filaEspera || false,
        autoFilaEspera: t.autoFilaEspera || false,
        exibirFilaEspera: t.exibirFilaEspera || false,
        camisetas: t.camisetas || false,
        sapatos: t.sapatos || false,
        atletaTrocaParceiro: t.atletaTrocaParceiro || false,
        exibirInscritos: t.exibirInscritos || false,
        autoCancNaoPagas: t.autoCancNaoPagas || false,
        validarElegibilidade: t.validarElegibilidade ?? false,
      });

      // Carregar local se existir
      if (t.local?.id) {
        getLocalById(t.local.id)
          .then((localData) => {
            setLocal(localData);
            setLocalFormData({
              nome: localData.nome,
              descricao: localData.descricao || '',
              urlLocalizacao: localData.urlLocalizacao || undefined,
              cep: localData.endereco?.cep || '',
              logradouro: localData.endereco?.logradouro || '',
              numero: localData.endereco?.numero || '',
              bairro: localData.endereco?.bairro || '',
              complemento: localData.endereco?.complemento || '',
              uf: localData.endereco?.cidade?.uf || '',
              cidadeId: localData.endereco?.cidade?.id || '',
            });
          })
          .catch(() => {
            // Ignorar erro ao carregar local
          });
      }
    }
  }, [torneioData, isEditMode]);

  // Validações
  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!form.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!form.dataInicio) {
      newErrors.dataInicio = 'Data de início é obrigatória';
    }

    if (!form.dataFim) {
      newErrors.dataFim = 'Data de fim é obrigatória';
    }

    if (form.dataInicio && form.dataFim && new Date(form.dataInicio) > new Date(form.dataFim)) {
      newErrors.dataInicio = 'Data de início não pode ser maior que data de fim';
    }

    if (
      form.dataInscricaoInicio &&
      form.dataInscricaoFim &&
      new Date(form.dataInscricaoInicio) > new Date(form.dataInscricaoFim)
    ) {
      newErrors.dataInscricaoInicio = 'Data de início de inscrição não pode ser maior que data de fim';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handlers
  const handleDescricaoSave = (html: string) => {
    setForm((prev) => ({ ...prev, descricao: html }));
  };

  const handleRegulamentoSave = (html: string) => {
    setForm((prev) => ({ ...prev, regulamento: html }));
  };

  const handleLocalSave = async (data: LocalModalForm) => {
    try {
      setSavingLocal(true);
      const localInput: LocalInput = {
        nome: data.nome,
        descricao: data.descricao || null,
        urlLocalizacao: data.urlLocalizacao,
        endereco: {
          cep: data.cep,
          logradouro: data.logradouro,
          numero: data.numero,
          bairro: data.bairro,
          complemento: data.complemento || null,
          cidadeId: data.cidadeId,
        },
      };

      const savedLocal = await upsertLocal({
        id: local?.id,
        local: localInput,
      });

      setLocal(savedLocal);
      setLocalFormData(data);
      setSnackbar({
        open: true,
        message: 'Local salvo com sucesso!',
        severity: 'success',
      });
      setIsLocalModalOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao salvar local',
        severity: 'error',
      });
    } finally {
      setSavingLocal(false);
    }
  };

  const handleSave = async () => {
    if (!validate()) {
      setSnackbar({
        open: true,
        message: 'Por favor, corrija os erros no formulário',
        severity: 'error',
      });
      return;
    }

    if (!ligaId) {
      setSnackbar({
        open: true,
        message: 'Liga não encontrada',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      // Salvar local primeiro se foi editado
      let localId: string | null = null;
      if (local) {
        // Se já existe local, usar o ID existente
        localId = local.id;
      } else if (localFormData && localFormData.nome) {
        // Se não existe local mas foi preenchido no formulário, criar novo
        const localInput: LocalInput = {
          nome: localFormData.nome,
          descricao: localFormData.descricao || null,
          endereco: {
            cep: localFormData.cep!,
            logradouro: localFormData.logradouro!,
            numero: localFormData.numero!,
            bairro: localFormData.bairro!,
            complemento: localFormData.complemento || null,
            cidadeId: localFormData.cidadeId!,
          },
        };
        const savedLocal = await upsertLocal({ local: localInput });
        localId = savedLocal.id;
      }

      if (isEditMode && id) {
        // Atualizar torneio existente
        await atualizarTorneio({
          id,
          nome: form.nome,
          username: form.username || '',
          subtitulo: form.subtitulo || null,
          descricao: form.descricao || null,
          regulamento: form.regulamento || null,
          dataInicio: form.dataInicio,
          dataFim: form.dataFim || null,
          dataInscricaoInicio: form.dataInscricaoInicio || null,
          dataInscricaoFim: form.dataInscricaoFim || null,
          localId: localId || null,
          status: form.status !== '' ? (form.status as StatusTorneio) : null,
          filaEspera: form.filaEspera,
          autoFilaEspera: form.autoFilaEspera,
          exibirFilaEspera: form.exibirFilaEspera,
          camisetas: form.camisetas,
          sapatos: form.sapatos,
          atletaTrocaParceiro: form.atletaTrocaParceiro,
          exibirInscritos: form.exibirInscritos,
          autoCancNaoPagas: form.autoCancNaoPagas,
          validarElegibilidade: form.validarElegibilidade,
        });
      } else {
        // Criar novo torneio
        const input: TorneioInput = {
          nome: form.nome,
          dataInicio: form.dataInicio,
          dataFim: form.dataFim,
          descricao: form.descricao || null,
          subtitulo: form.subtitulo || null,
          username: form.username || null,
          regulamento: form.regulamento || null,
          dataInscricaoInicio: form.dataInscricaoInicio || null,
          dataInscricaoFim: form.dataInscricaoFim || null,
          ligaId,
          status: StatusTorneio.AGENDADO,
          filaEspera: form.filaEspera,
          autoFilaEspera: form.autoFilaEspera,
          exibirFilaEspera: form.exibirFilaEspera,
          camisetas: form.camisetas,
          sapatos: form.sapatos,
          atletaTrocaParceiro: form.atletaTrocaParceiro,
          exibirInscritos: form.exibirInscritos,
          autoCancNaoPagas: form.autoCancNaoPagas,
          validarElegibilidade: form.validarElegibilidade,
        };

        const created = await criarTorneio(input);
        
        // Se tiver local, associar ao torneio criado
        if (localId && created.id) {
          await atualizarTorneio({
            id: created.id,
            localId,
          });
        }

        setSnackbar({
          open: true,
          message: 'Torneio criado com sucesso!',
          severity: 'success',
        });

        // Redirecionar para página de gerenciamento
        navigate(`/liga/tournament/${created.id}`);
        return;
      }

      setSnackbar({
        open: true,
        message: 'Torneio atualizado com sucesso!',
        severity: 'success',
      });

      // Redirecionar para página de gerenciamento
      navigate(`/liga/tournament/${id}`);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao salvar torneio',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  // Função auxiliar para acessibilidade das abas
  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
      href: `#simple-tabpanel-${index}`,
    };
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Converter dados do torneio para TorneioProfile usando o estado local
  const convertToTorneioProfile = (torneio: TorneioPorIdData['torneioPorId']): TorneioProfile => {
    return {
      id: torneio.id,
      nome: form.nome || torneio.nome || '',
      username: form.username || torneio.username || '',
      dataInicio: form.dataInicio || torneio.dataInicio || '',
      dataFim: form.dataFim || torneio.dataFim || '',
      dataInscricaoInicio: form.dataInscricaoInicio || torneio.dataInscricaoInicio || null,
      dataInscricaoFim: form.dataInscricaoFim || torneio.dataInscricaoFim || null,
      avatarUrl: torneio.avatarUrl || null,
      bannerUrl: torneio.bannerUrl || null,
      bannerVUrl: torneio.bannerVUrl || null,
      organizadores: torneio.organizadores || [],
    };
  };

  // Callback para quando o ProfilecardTournament salvar alterações
  const handleSaveTorneio = async (torneioData: TorneioProfile) => {
    if (!ligaId) {
      setSnackbar({
        open: true,
        message: 'Liga não encontrada',
        severity: 'error',
      });
      return;
    }

    setSaving(true);
    try {
      // Atualizar apenas os campos que vieram do ProfilecardTournament
      await atualizarTorneio({
        id: torneioData.id,
        nome: torneioData.nome,
        username: torneioData.username,
        dataInicio: torneioData.dataInicio,
        dataFim: torneioData.dataFim || null,
        dataInscricaoInicio: torneioData.dataInscricaoInicio || null,
        dataInscricaoFim: torneioData.dataInscricaoFim || null,
      });

      // Função para validar e formatar data
      const formatDate = (dateStr: string | null | undefined): string => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          return dateStr.slice(0, 10); // Garantir formato YYYY-MM-DD
        } catch {
          return '';
        }
      };

      // Atualizar o estado local com validação de datas
      setForm((prev) => ({
        ...prev,
        nome: torneioData.nome || prev.nome,
        username: torneioData.username || prev.username,
        dataInicio: formatDate(torneioData.dataInicio) || prev.dataInicio,
        dataFim: formatDate(torneioData.dataFim) || prev.dataFim,
        dataInscricaoInicio: formatDate(torneioData.dataInscricaoInicio) || prev.dataInscricaoInicio,
        dataInscricaoFim: formatDate(torneioData.dataInscricaoFim) || prev.dataInscricaoFim,
      }));

      setSnackbar({
        open: true,
        message: 'Torneio atualizado com sucesso!',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.message || 'Erro ao salvar torneio',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Callbacks para atualização de avatar e banner
  const handleAvatarUpdated = (newUrl: string) => {
    // O ProfilecardTournament já atualiza internamente, mas podemos atualizar o estado se necessário
  };

  const handleBannerUpdated = (newUrl: string) => {
    // O ProfilecardTournament já atualiza internamente, mas podemos atualizar o estado se necessário
  };

  const handleBannerVUpdated = (newUrl: string) => {
    // O ProfilecardTournament já atualiza internamente, mas podemos atualizar o estado se necessário
  };

  // Verificações de permissão e loading
  if (profileLoading || permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  if (!canManageTournaments) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-warning" role="alert">
                        <h5 className="alert-heading">Acesso Negado</h5>
                        <p className="mb-0">
                          Você não tem permissão para criar/editar torneios desta Liga.
                        </p>
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/dashboard')}
                      >
                        Voltar ao Dashboard
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isEditMode && loadingTorneio) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (isEditMode && (errorTorneio || !torneioData?.torneioPorId)) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Erro ao carregar torneio. Tente novamente.
                      </div>
                      <button
                        className="btn btn-primary mt-3"
                        onClick={() => navigate('/liga/tournaments')}
                      >
                        Voltar aos Torneios
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      {saving && (
        <div 
          className="position-fixed w-100 h-100 d-flex align-items-center justify-content-center" 
          style={{ 
            top: 0, 
            left: 0, 
            backgroundColor: 'rgba(255, 255, 255, 0.1)', 
            zIndex: 9999 
          }}
        >
          <div 
            className="bg-white rounded p-3 shadow d-flex flex-column align-items-center"
            style={{ width: '120px', height: '120px' }}
          >
            <div className="spinner-border text-primary mb-2" role="status" style={{ width: '2rem', height: '2rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <small className="text-muted">Carregando...</small>
          </div>
        </div>
      )}
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="row feed-body">
              <div className="col-xl-12">
                <div className="mb-3">
                  <PageBreadcrumb
                    items={[
                      { label: 'Dashboard', href: '/liga/dashboard' },
                      { label: 'Torneios', href: '/liga/tournaments' },
                      ...(isEditMode && torneioData?.torneioPorId
                        ? [
                            {
                              label: torneioData.torneioPorId.nome,
                              href: `/liga/tournament/${id}`,
                            },
                          ]
                        : [{ label: 'Novo Torneio' }]),
                    ]}
                    onBack={() =>
                      navigate(isEditMode && id ? `/liga/tournament/${id}` : '/liga/tournaments')
                    }
                    variant="bootstrap"
                  />
                </div>
                {/* ProfilecardTournament - apenas em modo edição */}
                {isEditMode && torneioData?.torneioPorId && (
                  <ProfilecardTournament
                    torneio={convertToTorneioProfile(torneioData.torneioPorId)}
                    onSaveTorneio={handleSaveTorneio}
                    onAvatarUpdated={handleAvatarUpdated}
                    onBannerUpdated={handleBannerUpdated}
                    onBannerVUpdated={handleBannerVUpdated}
                  />
                )}

                <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                  <div className="card-body p-4">
                    <div className="mb-4">
                      <h3 className="fw-700 mb-0">
                        {isEditMode ? 'Editar Torneio' : 'Criar Novo Torneio'}
                      </h3>
                    </div>

                    {/* Abas - apenas em modo edição */}
                    {isEditMode && (
                      <Tabs
                        value={tab}
                        onChange={handleTabChange}
                        aria-label="abas do torneio"
                        indicatorColor="primary"
                        textColor="primary"
                        className="mb-4"
                      >
                        <Tab label="Informações" {...a11yProps(0)} />
                        <Tab label="Organizadores" {...a11yProps(1)} />
                        <Tab label="Patrocinadores" {...a11yProps(2)} />
                        <Tab label="Descontos" {...a11yProps(3)} />
                      </Tabs>
                    )}

                    {/* Tabpanel 0 - Informações */}
                    <div
                      role="tabpanel"
                      hidden={isEditMode && tab !== 0}
                      id={isEditMode ? 'simple-tabpanel-0' : undefined}
                      aria-labelledby={isEditMode ? 'simple-tab-0' : undefined}
                    >
                      {/* Campos de texto simples - ocultos em modo edição */}
                    {!isEditMode && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">
                            Nome <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${errors.nome ? 'is-invalid' : ''}`}
                            value={form.nome}
                            onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                          />
                          {errors.nome && <div className="text-danger small">{errors.nome}</div>}
                        </div>

                        <div className="mb-3">
                          <label className="form-label">Username</label>
                          <input
                            type="text"
                            className="form-control"
                            value={form.username}
                            onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                          />
                        </div>
                      </>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Subtítulo</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.subtitulo}
                        onChange={(e) => setForm((prev) => ({ ...prev, subtitulo: e.target.value }))}
                      />
                    </div>

                    {/* Campos de data */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Data de Início <span className="text-danger">*</span>
                        </label>
                        <DatePicker
                          selected={form.dataInicio ? (() => {
                            try {
                              return new Date(form.dataInicio + 'T12:00:00');
                            } catch {
                              return null;
                            }
                          })() : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setForm((prev) => ({
                                ...prev,
                                dataInicio: `${year}-${month}-${day}`,
                              }));
                            } else {
                              setForm((prev) => ({ ...prev, dataInicio: '' }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          className={`form-control ${errors.dataInicio ? 'is-invalid' : ''}`}
                          placeholderText="Selecione a data"
                        />
                        {errors.dataInicio && (
                          <div className="text-danger small">{errors.dataInicio}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">
                          Data de Fim <span className="text-danger">*</span>
                        </label>
                        <DatePicker
                          selected={form.dataFim ? (() => {
                            try {
                              return new Date(form.dataFim + 'T12:00:00');
                            } catch {
                              return null;
                            }
                          })() : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setForm((prev) => ({
                                ...prev,
                                dataFim: `${year}-${month}-${day}`,
                              }));
                            } else {
                              setForm((prev) => ({ ...prev, dataFim: '' }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          className={`form-control ${errors.dataFim ? 'is-invalid' : ''}`}
                          placeholderText="Selecione a data"
                          minDate={form.dataInicio ? (() => {
                            try {
                              return new Date(form.dataInicio + 'T12:00:00');
                            } catch {
                              return undefined;
                            }
                          })() : undefined}
                        />
                        {errors.dataFim && (
                          <div className="text-danger small">{errors.dataFim}</div>
                        )}
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Data de Início das Inscrições</label>
                        <DatePicker
                          selected={form.dataInscricaoInicio ? (() => {
                            try {
                              return new Date(form.dataInscricaoInicio + 'T12:00:00');
                            } catch {
                              return null;
                            }
                          })() : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setForm((prev) => ({
                                ...prev,
                                dataInscricaoInicio: `${year}-${month}-${day}`,
                              }));
                            } else {
                              setForm((prev) => ({ ...prev, dataInscricaoInicio: '' }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          className={`form-control ${errors.dataInscricaoInicio ? 'is-invalid' : ''}`}
                          placeholderText="Selecione a data"
                        />
                        {errors.dataInscricaoInicio && (
                          <div className="text-danger small">{errors.dataInscricaoInicio}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Data de Fim das Inscrições</label>
                        <DatePicker
                          selected={form.dataInscricaoFim ? (() => {
                            try {
                              return new Date(form.dataInscricaoFim + 'T12:00:00');
                            } catch {
                              return null;
                            }
                          })() : null}
                          onChange={(date: Date | null) => {
                            if (date) {
                              const year = date.getFullYear();
                              const month = String(date.getMonth() + 1).padStart(2, '0');
                              const day = String(date.getDate()).padStart(2, '0');
                              setForm((prev) => ({
                                ...prev,
                                dataInscricaoFim: `${year}-${month}-${day}`,
                              }));
                            } else {
                              setForm((prev) => ({ ...prev, dataInscricaoFim: '' }));
                            }
                          }}
                          dateFormat="dd/MM/yyyy"
                          locale="pt-BR"
                          className="form-control"
                          placeholderText="Selecione a data"
                          minDate={form.dataInscricaoInicio ? (() => {
                            try {
                              return new Date(form.dataInscricaoInicio + 'T12:00:00');
                            } catch {
                              return undefined;
                            }
                          })() : undefined}
                        />
                      </div>
                    </div>

                    {/* Campo de status - somente em edição */}
                    {isEditMode && (
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={form.status}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, status: e.target.value as StatusTorneio | '' }))
                          }
                        >
                          <option value="">Selecione um status</option>
                          {Object.values(StatusTorneio).map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Descrição com TinyMCE */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label">Descrição</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setIsDescricaoModalOpen(true)}
                        >
                          Editar
                        </button>
                      </div>
                      <div
                        className="border rounded p-3"
                        style={{ minHeight: '100px' }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(form.descricao || '<p class="text-muted">Sem descrição</p>'),
                        }}
                      />
                    </div>

                    {/* Regulamento com TinyMCE */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label">Regulamento</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setIsRegulamentoModalOpen(true)}
                        >
                          Editar
                        </button>
                      </div>
                      <div
                        className="border rounded p-3"
                        style={{ minHeight: '100px' }}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(
                            form.regulamento || '<p class="text-muted">Sem regulamento</p>'
                          ),
                        }}
                      />
                    </div>

                    {/* Local */}
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label">Local</label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setIsLocalModalOpen(true)}
                        >
                          {local ? 'Editar Local' : 'Adicionar Local'}
                        </button>
                      </div>
                      {local ? (
                        <div className="border rounded p-3">
                          <p className="mb-1">
                            <strong>{local.nome}</strong>
                          </p>
                          {local.descricao && <p className="mb-1 text-muted">{local.descricao}</p>}
                          {local.endereco && (
                            <p className="mb-0 small">
                              {local.endereco.logradouro}, {local.endereco.numero} - {local.endereco.bairro}
                              <br />
                              {local.endereco.cidade.nome}/{local.endereco.cidade.uf} - CEP: {local.endereco.cep}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="border rounded p-3 text-muted">
                          Nenhum local cadastrado
                        </div>
                      )}
                    </div>

                    {/* Seção de checkboxes */}
                    <div className="mb-3">
                      <label className="form-label mb-3 d-block">Configurações</label>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.filaEspera}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, filaEspera: e.target.checked }))
                              }
                              id="filaEspera"
                            />
                            <label className="form-check-label" htmlFor="filaEspera">
                              Fila de Espera
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.autoFilaEspera}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, autoFilaEspera: e.target.checked }))
                              }
                              id="autoFilaEspera"
                            />
                            <label className="form-check-label" htmlFor="autoFilaEspera">
                              Auto Fila de Espera
                            </label>
                          </div>
                          <div className="form-check mb-2 d-flex align-items-center gap-2">
                            <div>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={form.validarElegibilidade}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    validarElegibilidade: e.target.checked,
                                  }))
                                }
                                id="validarElegibilidade"
                              />
                              <label
                                className="form-check-label"
                                htmlFor="validarElegibilidade"
                              >
                                Validar elegibilidade na inscrição
                              </label>
                            </div>
                            <Tooltip
                              title="Quando ativada, a inscrição valida automaticamente gênero e idade do atleta contra as regras da categoria."
                              arrow
                            >
                              <IconButton
                                size="small"
                                aria-label="Informações sobre elegibilidade"
                                tabIndex={0}
                              >
                                <i
                                  className="bi bi-info-circle"
                                  style={{ fontSize: '1rem' }}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.exibirFilaEspera}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, exibirFilaEspera: e.target.checked }))
                              }
                              id="exibirFilaEspera"
                            />
                            <label className="form-check-label" htmlFor="exibirFilaEspera">
                              Exibir Fila de Espera
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.camisetas}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, camisetas: e.target.checked }))
                              }
                              id="camisetas"
                            />
                            <label className="form-check-label" htmlFor="camisetas">
                              Camisetas
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.sapatos}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, sapatos: e.target.checked }))
                              }
                              id="sapatos"
                            />
                            <label className="form-check-label" htmlFor="sapatos">
                              Sapatos
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.atletaTrocaParceiro}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, atletaTrocaParceiro: e.target.checked }))
                              }
                              id="atletaTrocaParceiro"
                            />
                            <label className="form-check-label" htmlFor="atletaTrocaParceiro">
                              Atleta Troca Parceiro
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.exibirInscritos}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, exibirInscritos: e.target.checked }))
                              }
                              id="exibirInscritos"
                            />
                            <label className="form-check-label" htmlFor="exibirInscritos">
                              Exibir Inscritos
                            </label>
                          </div>
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.autoCancNaoPagas}
                              onChange={(e) =>
                                setForm((prev) => ({ ...prev, autoCancNaoPagas: e.target.checked }))
                              }
                              id="autoCancNaoPagas"
                            />
                            <label className="form-check-label" htmlFor="autoCancNaoPagas">
                              Auto Cancelar Não Pagas
                            </label>
                          </div>
                          {form.autoCancNaoPagas && (
                            <div className="text-muted font-xssss ms-4 mb-2">
                              Inscrições não pagas serão canceladas automaticamente. Prazo de pagamento: 24 horas.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                      {/* Botões de ação */}
                      <div className="d-flex justify-content-end gap-2 mt-4">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => navigate(isEditMode ? `/liga/tournament/${id}` : '/liga/tournaments')}
                          disabled={saving}
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </div>

                    {/* Tabpanel 1 - Organizadores */}
                    {isEditMode && (
                      <div
                        role="tabpanel"
                        hidden={tab !== 1}
                        id="simple-tabpanel-1"
                        aria-labelledby="simple-tab-1"
                      >
                        <TournamentOrganizersTab
                          torneioId={id!}
                          canEditLiga={canManageTournaments}
                          onNotify={(message, severity) => {
                            setSnackbar({
                              open: true,
                              message,
                              severity,
                            });
                          }}
                        />
                      </div>
                    )}

                    {/* Tabpanel 2 - Patrocinadores */}
                    {isEditMode && (
                      <div
                        role="tabpanel"
                        hidden={tab !== 2}
                        id="simple-tabpanel-2"
                        aria-labelledby="simple-tab-2"
                      >
                        <TournamentSponsorsTab
                          torneioId={id!}
                          canEditLiga={canManageTournaments}
                          onNotify={(message, severity) => {
                            setSnackbar({
                              open: true,
                              message,
                              severity,
                            });
                          }}
                        />
                      </div>
                    )}

                    {/* Tabpanel 3 - Descontos */}
                    {isEditMode && (
                      <div
                        role="tabpanel"
                        hidden={tab !== 3}
                        id="simple-tabpanel-3"
                        aria-labelledby="simple-tab-3"
                      >
                        <TournamentDiscountsTab
                          torneioId={id!}
                          canEditLiga={canManageTournaments}
                          onNotify={(message, severity) => {
                            setSnackbar({
                              open: true,
                              message,
                              severity,
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modais */}
      <DescricaoModal
        open={isDescricaoModalOpen}
        initialValue={form.descricao}
        onSave={handleDescricaoSave}
        onClose={() => setIsDescricaoModalOpen(false)}
      />

      <RegulamentoEditModal
        open={isRegulamentoModalOpen}
        initialValue={form.regulamento}
        onSave={handleRegulamentoSave}
        onClose={() => setIsRegulamentoModalOpen(false)}
      />

      <LocalModal
        open={isLocalModalOpen}
        initialValues={localFormData || undefined}
        saving={savingLocal}
        onSave={handleLocalSave}
        onClose={() => setIsLocalModalOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditLeagueTournament;

